// IndexedDB cache for the latest /auth/me payload.

import { openDB, type IDBPDatabase } from 'idb'
import type { Student } from '../hooks/useAuth'

// ── Constants ────────────────────────────────────────────────────────────────

const DB_NAME    = 'tutor-auth-v1'
const DB_VERSION = 1
const STORE      = 'auth'
const LAST_AUTH_STORAGE_KEY = 'tutor_auth_last_snapshot'

// Old rows are treated as stale to avoid "ghost login" behavior.
export const AUTH_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000

// ── Types ────────────────────────────────────────────────────────────────────

export interface CachedAuth {
  firebase_uid: string
  student:      Student
  cached_at:    number
}

let _db: IDBPDatabase | null = null

function readStoredSnapshot(): CachedAuth | null {
  try {
    const raw = window.localStorage.getItem(LAST_AUTH_STORAGE_KEY)
    if (!raw) return null
    const row = JSON.parse(raw) as CachedAuth
    if (Date.now() - row.cached_at > AUTH_CACHE_TTL_MS) {
      window.localStorage.removeItem(LAST_AUTH_STORAGE_KEY)
      return null
    }
    return row
  } catch {
    return null
  }
}

function writeStoredSnapshot(row: CachedAuth) {
  try {
    window.localStorage.setItem(LAST_AUTH_STORAGE_KEY, JSON.stringify(row))
  } catch {
    /* best-effort mirror */
  }
}

function clearStoredSnapshot(uid?: string) {
  try {
    const existing = readStoredSnapshot()
    if (!existing) return
    if (!uid || existing.firebase_uid === uid) {
      window.localStorage.removeItem(LAST_AUTH_STORAGE_KEY)
    }
  } catch {
    /* best-effort clear */
  }
}

async function getDb(): Promise<IDBPDatabase> {
  if (!_db) {
    _db = await openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'firebase_uid' })
        }
      },
      terminated() {
        _db = null
      },
    })
  }
  return _db
}

// Retry once when IndexedDB closes underneath us during dev/HMR.
async function withDbRetry<T>(op: (db: IDBPDatabase) => Promise<T>): Promise<T> {
  try {
    return await op(await getDb())
  } catch (err: any) {
    const closing =
      err?.name === 'InvalidStateError' ||
      /database connection is closing/i.test(err?.message ?? '')
    if (!closing) throw err
    _db = null
    return op(await getDb())
  }
}

// Returns null for missing or stale rows.
export async function readAuthCache(uid: string): Promise<CachedAuth | null> {
  try {
    const row = await withDbRetry(db => db.get(STORE, uid)) as CachedAuth | undefined
    if (!row) return null
    if (Date.now() - row.cached_at > AUTH_CACHE_TTL_MS) {
      withDbRetry(db => db.delete(STORE, uid)).catch(() => {})
      return null
    }
    return row
  } catch (err) {
    console.warn('[authCache] read failed', err)
    return null
  }
}

export function readLatestAuthSnapshotSync(): CachedAuth | null {
  return readStoredSnapshot()
}

export async function readLatestAuthSnapshot(): Promise<CachedAuth | null> {
  const stored = readStoredSnapshot()
  if (stored) return stored
  try {
    const rows = await withDbRetry(db => db.getAll(STORE)) as CachedAuth[]
    const freshRows = rows.filter((row) => Date.now() - row.cached_at <= AUTH_CACHE_TTL_MS)
    if (freshRows.length === 0) return null
    const latest = freshRows.sort((a, b) => b.cached_at - a.cached_at)[0] ?? null
    if (latest) writeStoredSnapshot(latest)
    return latest
  } catch (err) {
    console.warn('[authCache] readLatest failed', err)
    return null
  }
}

export async function writeAuthCache(payload: { firebase_uid: string; student: Student }): Promise<void> {
  const row: CachedAuth = {
    firebase_uid: payload.firebase_uid,
    student:      payload.student,
    cached_at:    Date.now(),
  }
  writeStoredSnapshot(row)
  try {
    await withDbRetry(db => db.put(STORE, row))
  } catch (err) {
    console.warn('[authCache] write failed', err)
  }
}

// Clears one cached user, or the whole store when no UID is provided.
export async function clearAuthCache(uid?: string): Promise<void> {
  clearStoredSnapshot(uid)
  try {
    await withDbRetry(async db => {
      if (uid) await db.delete(STORE, uid)
      else     await db.clear(STORE)
    })
  } catch (err) {
    console.warn('[authCache] clear failed', err)
  }
}
